import { TOOL_ICONS, TOOL_TIPS } from 'modules/site-walk-gui/helper/constants';
import { Tool, Polygon } from 'modules/site-walk-gui/helper/types';
import { PropertiesPanel } from '../PropertiesPanel/PropertiesPanel';
import { IFloorPlanModel } from '@api/models';

interface SidebarProps {
    isEditMode: boolean;
    activeTool: Tool;
    polygons: Polygon[];
    selectedPolygonId: string | null;
    selectedPolygon: Polygon | null;
    onSelectTool: (tool: Tool) => void;
    onSelectPolygon: (id: string) => void;
    onChangePolygonColor: (color: string) => void;
    onChangePolygonFloorPlan: (floorPlan: string) => void;
    floorPlans: IFloorPlanModel[];
    floorPlanLoading: boolean;
}

export const Sidebar = ({
    isEditMode,
    activeTool,
    polygons,
    selectedPolygonId,
    selectedPolygon,
    onSelectTool,
    onSelectPolygon,
    onChangePolygonColor,
    onChangePolygonFloorPlan,
    floorPlans,
    floorPlanLoading,
}: SidebarProps) => (
    <div className="w-56 flex-shrink-0 bg-[#1e293b] border-r border-white/10 flex flex-col">
        {/* Tool picker */}
        <div className="px-4 py-3 border-b border-white/10">
            <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">Tools</span>
        </div>
        <div className="p-3 flex flex-col gap-2">
            {(['polygon', 'select'] as Tool[]).map((tool) => (
                <button
                    key={tool}
                    onClick={() => onSelectTool(tool)}
                    className={`flex items-center gap-2 px-3 py-2 rounded text-sm capitalize transition-all ${
                        activeTool === tool
                            ? 'bg-blue-600 text-white'
                            : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                >
                    {TOOL_ICONS[tool]}
                    {tool}
                </button>
            ))}
        </div>

        {/* Zone list */}
        <div className="px-4 py-3 border-t border-b border-white/10 mt-2">
            <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">
                Zones ({polygons.length})
            </span>
        </div>
        <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-1">
            {polygons.length === 0 && <p className="text-xs text-slate-600 px-2 py-3">No zones yet</p>}
            {polygons.map((polygon) => (
                <div
                    key={polygon.id}
                    onClick={() => onSelectPolygon(polygon.id)}
                    className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer text-sm transition-all ${
                        selectedPolygonId === polygon.id
                            ? 'bg-white/10 text-white'
                            : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                >
                    <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: polygon.color }} />
                    <span className="truncate">
                        {polygon.id} (
                        {floorPlans?.find((floorPlan) => floorPlan._id === polygon.id)?.name ?? 'No floor plan'})
                    </span>
                    <span className="ml-auto text-xs text-slate-600">{polygon.points.length}pt</span>
                </div>
            ))}
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
            {isEditMode && selectedPolygon ? (
                <PropertiesPanel
                    polygon={selectedPolygon}
                    onChangeColor={onChangePolygonColor}
                    onChangeFloorPlan={onChangePolygonFloorPlan}
                    floorPlans={floorPlans}
                    floorPlanLoading={floorPlanLoading}
                />
            ) : (
                <div className="p-4 text-xs text-slate-500">
                    {isEditMode ? 'Select a polygon to edit its properties' : 'Enter edit mode to modify zones'}
                </div>
            )}
        </div>

        {/* Context-sensitive tips */}
        <div className="p-3 border-t border-white/10 text-[10px] text-slate-600 leading-5 mt-auto">
            {TOOL_TIPS[activeTool]}
        </div>
    </div>
);
